import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import {z} from 'zod';
import {campaignPlacementsApi} from 'common/src/modules/campaignPlacements/campaignPlacements.api';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {CLStore} from 'common/src/redux/store';
import {Placement_UI} from 'common/src/modules/campaignPlacements/placements.types';
import getPlacementKey from '../utils/getPlacementKey';
import fetchCampaignPlacements from 'common/src/modules/campaigns/store/campaignTree/thunks/fetchCampaignPlacements';

const deletePlacementsToolSchema = z.object({
  placementsKeys: z.array(
    z.object({
      plcId: z.string(),
      dspId: z.number(),
    }),
  ),
});

export default function deletePlacementsTool(store: CLStore) {
  async function execute(params: ToolExecuteParams<typeof deletePlacementsToolSchema>) {
    const {input} = params;
    const {placementsKeys} = input;
    const campaignId = store.getState().campaignPage.campaignTree.campaign?.id;
    const campaignVersionId = store.getState().campaignPage.campaignTree.campaignVersions[0]?.id;

    if (!campaignId || !campaignVersionId) {
      return toolError('Campaign ID or campaign version ID is not set');
    }

    const existingPlacements = store.getState().campaignPage.campaignTree.campaignPlacements;

    const existingPlacementsByKeys = existingPlacements.reduce(
      (acc, placement) => {
        acc[getPlacementKey(placement.placementId, placement.dspId)] = placement;

        return acc;
      },
      {} as Record<string, Placement_UI>,
    );

    const notExistingPlacementsKeys = placementsKeys.filter(
      (key) => !existingPlacementsByKeys[getPlacementKey(key.plcId, key.dspId)],
    );

    if (notExistingPlacementsKeys.length > 0) {
      return toolError(
        `Some placements do not exist: ${notExistingPlacementsKeys.map((key) => `${key.plcId} - ${key.dspId}`).join(', ')}`,
      );
    }

    await campaignPlacementsApi
      .deletePlacement_v2({
        campaignId,
        campaignVersionId,
        placementsIds: placementsKeys.map(
          (key) => existingPlacementsByKeys[getPlacementKey(key.plcId, key.dspId)].id,
        ),
      })
      .then(() => {
        store.dispatch(fetchCampaignPlacements({campaignId, campaignVersionId}));
      });

    return toolResult(`Placements deleted successfully`);
  }

  return tool({
    name: 'deletePlacements',
    displayName: 'Deleting placements',
    description: 'Delete placements from the campaign',
    inputSchema: deletePlacementsToolSchema,
    execute,
  });
}
